import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Switch, Modal, Button, Spin, Alert } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { BudgetStatus } from 'types/marketing';
import type { IAdBudgetDisplay, BudgetPeriod } from 'types/marketing';
import usePracticePoolBudget from 'hooks/marketing/usePracticePoolBudget';
import usePracticeOverview from 'hooks/marketing/usePracticeOverview';
import usePracticeByProvider from 'hooks/marketing/usePracticeByProvider';
import { formatUTCTime, isProviderBudgetExhausted, isProviderListed, poolBudgetToDisplay } from 'utils/marketing';
import GroupBudgetDistribution from 'pages/dashboard/Marketing/components/GroupBudgetDistribution';
import StrategyTable from 'pages/ProListingSetup/components/StrategyTable';
import GroupSpecialtyOverrides from 'pages/dashboard/Marketing/components/GroupSpecialtyOverrides';
import type { SpecialtyOverrides } from 'pages/dashboard/Marketing/components/GroupSpecialtyOverrides';
import GroupConfirmSaveModal from 'pages/dashboard/Marketing/components/GroupConfirmSaveModal';
import CampaignIntro from './CampaignIntro';
import StickyFooterActions from './StickyFooterActions';
import AddProviderModal from 'components/AddProviderModal';
import PaymentContent from 'components/PaymentContent';
import WebinarBooking from 'components/WebinarBooking';
import StripeIcon from 'assets/common/stripe.png';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { CALENDLY_LINK } from 'constants/common';
import { EWebinarScheduleType } from 'types/common';
import { getHelloKlarityHost } from 'utils/common';
import s from './s.module.less';

const DefaultSpecialtyBid = 60;

function getDateRange(weeks: number) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - weeks * 7);
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    };
}

const GroupCampaignSetup: React.FC = () => {
    const [overview, overviewLoading, refetchOverview] = usePracticeOverview();
    const [poolBudget, poolBudgetLoading, refetchPoolBudget] = usePracticePoolBudget();
    const [providerRows, , refetchProviderRows] = usePracticeByProvider(getDateRange(4));
    const [homeData] = useProviderHomeInfo();

    const hasJoinedProListing = !overviewLoading && overview?.createdAt;
    const proListingCount = overview?.onProListing ?? 0;
    const stdListingCount = overview?.totalProviders ?? 0;
    const isPremiumTier = proListingCount >= 5;
    const isPreLaunch = !hasJoinedProListing;
    const isPPAProvider = !!homeData?.ppaProvider;

    const [setupRevealed, setSetupRevealed] = useState(false);
    const [strategyData, setStrategyData] = useState<IAdBudgetDisplay>({
        budgetPeriod: 'WEEKLY' as BudgetPeriod,
        budgetCap: 300,
        generalBidAmount: DefaultSpecialtyBid,
        enableAdvancedSettings: false,
    });
    const [advancedSettings, setAdvancedSettings] = useState(false);

    const [budgetExcluded, setBudgetExcluded] = useState<Set<number>>(new Set());

    const activeProviderCount = useMemo(() => {
        if (providerRows.length > 0) {
            return providerRows.filter((r) => !budgetExcluded.has(r.providerId)).length;
        }
        return Math.max(stdListingCount - budgetExcluded.size, 0);
    }, [providerRows, stdListingCount, budgetExcluded]);

    const budgetSummary = useMemo(() => {
        const cap = strategyData.budgetCap || 0;
        const count = activeProviderCount || 0;
        const perProvider = count > 0 ? Math.round(cap / count) : 0;
        const periodLabel = strategyData.budgetPeriod === 'MONTHLY' ? 'mo' : 'wk';
        return { cap, providerCount: count, perProvider, periodLabel };
    }, [strategyData.budgetCap, strategyData.budgetPeriod, activeProviderCount]);

    const toggleBudgetProvider = (id: number) => {
        setBudgetExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const [strategyTableErrors, setStrategyTableErrors] = useState<{ bidError: string; budgetError: string }>({
        bidError: '',
        budgetError: '',
    });
    const [specialtyBidErrors] = useState<Record<string, string>>({});
    const [initialOverrides, setInitialOverrides] = useState<SpecialtyOverrides | undefined>();
    const [specialtyOverrides, setSpecialtyOverrides] = useState<SpecialtyOverrides | undefined>();
    const handleOverridesChange = useCallback((o: SpecialtyOverrides) => setSpecialtyOverrides(o), []);

    const [confirmSaveModalOpen, setConfirmSaveModalOpen] = useState(false);
    const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
    const [showWebinarBooking, setShowWebinarBooking] = useState(false);
    const [addProviderOpen, setAddProviderOpen] = useState(false);

    useEffect(() => {
        if (poolBudget) {
            const { strategyData: display, initialOverrides: overrides } = poolBudgetToDisplay(poolBudget);
            setStrategyData(display);
            setAdvancedSettings(display.enableAdvancedSettings || false);
            setInitialOverrides(overrides);
        }
    }, [poolBudget]);

    useEffect(() => {
        if (providerRows.length === 0) return;
        // Providers that aren't listed can't join the campaign — always exclude them.
        const notListed = providerRows.filter((r) => !isProviderListed(r)).map((r) => r.providerId);
        if (!hasJoinedProListing) {
            // Pre-launch (not yet joined): all listed providers default to on.
            setBudgetExcluded(new Set(notListed));
        } else {
            setBudgetExcluded(
                new Set(providerRows.filter((r) => !r.onProListing || !isProviderListed(r)).map((r) => r.providerId)),
            );
        }
    }, [providerRows, hasJoinedProListing]);

    useEffect(() => {
        if (!specialtyOverrides) return;
        const { disabledSpecs, disabledProvSpecs } = specialtyOverrides;

        setBudgetExcluded((prev) => {
            let changed = false;
            const next = new Set(prev);
            providerRows.forEach((p) => {
                if (prev.has(p.providerId)) return;
                const specKeys = p.services
                    ?.filter((svc) => svc.specialtyName)
                    .map((svc) => svc.specialtyName.toLowerCase().replace(/\s+/g, '_'));
                if (!specKeys?.length) return;
                const allDisabled = specKeys.every(
                    (k) => disabledSpecs.has(k) || disabledProvSpecs[k]?.has(p.providerId),
                );
                if (allDisabled) {
                    next.add(p.providerId);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [specialtyOverrides, providerRows]);

    const changeAdvancedSettings = (checked: boolean) => {
        setAdvancedSettings(checked);
        setStrategyData((prev) => ({ ...prev, enableAdvancedSettings: checked }));
    };

    const validateForm = () => {
        const hasStrategyErrors = strategyTableErrors.bidError || strategyTableErrors.budgetError;
        const hasSpecialtyErrors = Object.keys(specialtyBidErrors).length > 0;
        return !hasStrategyErrors && !hasSpecialtyErrors;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        if (isPPAProvider || hasJoinedProListing) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    // Budget-exhausted reminders: whole pool used up vs. individual providers used up.
    const poolExhausted = overview?.poolStatus === BudgetStatus.BUDGET_EXHAUSTED;
    const exhaustedProviderCount = providerRows.filter(isProviderBudgetExhausted).length;
    const showBudgetBanner = poolExhausted || exhaustedProviderCount > 0;

    const budgetBannerRender = () => {
        let message = '';
        if (poolExhausted) {
            message =
                "All of your providers' Pro-Listings are paused due to insufficient budget. Increase budgets to resume visibility on Klarity Health.";
        } else if (exhaustedProviderCount > 0) {
            message = `${exhaustedProviderCount} of ${proListingCount} providers' Pro-Listings are paused due to insufficient budget. Increase their budgets to resume visibility on Klarity Health.`;
        }
        return (
            message && (
                <Alert
                    className={s.budgetBanner}
                    message={message}
                    type="info"
                    showIcon
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    }
                />
            )
        );
    };

    // The practice launched Pro-Listing at some point but has nobody on it now — the whole campaign
    // is paused, so the "live" banner would be a lie. Offer the way back instead.
    const campaignPaused = !!overview?.createdAt && proListingCount === 0;
    const pausedOn = formatUTCTime(overview?.lastModifiedAt || overview?.createdAt || undefined);

    // Resuming is a starting point, not a commitment: open the advanced panel with every listed
    // provider switched back on, then let the user trim the list before saving.
    const handleResumeNow = () => {
        setAdvancedSettings(true);
        setStrategyData((prev) => ({ ...prev, enableAdvancedSettings: true }));
        setBudgetExcluded(new Set(providerRows.filter((r) => !isProviderListed(r)).map((r) => r.providerId)));
    };

    const pausedBannerRender = () => (
        <div className={s.pausedBanner}>
            <div className={s.pausedBannerInner}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                        fill="#FBBF24"
                    />
                </svg>
                <p className={s.pausedBannerText}>
                    Your Pro-Listing campaign was paused
                    {pausedOn ? (
                        <>
                            {' on '}
                            <strong>{pausedOn}</strong>
                        </>
                    ) : null}
                    . You can resume it anytime to continue reaching more patients.
                </p>
                <button type="button" className={s.resumeBtn} onClick={handleResumeNow}>
                    Resume now
                </button>
            </div>
        </div>
    );

    const liveBannerRender = () => {
        // The paused / budget-exhausted banners take priority over the "live" banner.
        if (!hasJoinedProListing || campaignPaused || showBudgetBanner) return null;

        const belowThreshold = proListingCount < 5;
        const need = 5 - proListingCount;

        const title = isPremiumTier ? "You're on the Premium tier" : 'Your Pro-Listing campaign is live';

        const text = isPremiumTier
            ? `${proListingCount === stdListingCount ? 'All ' : ''}${proListingCount} of ${stdListingCount} providers on Pro-Listing — premium features are live.`
            : `${proListingCount} of ${stdListingCount} provider${stdListingCount === 1 ? '' : 's'} on Pro-Listing — ${belowThreshold ? `add ${need} more to enable Premium Placement.` : 'bidding is active. Adjust anytime below.'}`;

        return (
            <div className={s.liveBanner}>
                <div className={s.liveBannerIcon}>&#10003;</div>
                <div className={s.liveBannerContent}>
                    <div className={s.liveBannerTitle}>{title}</div>
                    <div className={s.liveBannerText}>{text}</div>
                </div>
                <div className={s.liveBannerActions}>
                    {belowThreshold && (
                        <Button className={s.addProvidersBtn} onClick={() => setAddProviderOpen(true)}>
                            + Add more providers
                        </Button>
                    )}
                    {isPremiumTier && overview?.slugName && (
                        <Button
                            className={s.viewPracticeBtn}
                            onClick={() => window.open(`${getHelloKlarityHost()}/group/${overview.slugName}`, '_blank')}
                        >
                            View group practice page
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    if (overviewLoading || poolBudgetLoading) {
        return <Spin spinning />;
    }

    if (isPreLaunch && !setupRevealed && !poolExhausted) {
        return (
            <CampaignIntro
                isGroupPractice
                stdListingCount={stdListingCount}
                proListingCount={proListingCount}
                onSetupClick={() => setSetupRevealed(true)}
            />
        );
    }

    return (
        <div className={s.setupWrap}>
            {/* Whole campaign paused > budget-exhausted reminder > live banner */}
            {campaignPaused && pausedBannerRender()}

            {/* Budget-exhausted reminder (pool or individual providers) */}
            {!campaignPaused && budgetBannerRender()}

            {/* Live banner */}
            {liveBannerRender()}

            {/* Strategy card */}
            <div className={s.strategyCard}>
                <div className={s.strategyCardHeader}>
                    <div className={s.strategyTitle}>Set your campaign strategy</div>
                    <div className={s.strategySubtitle}>
                        Boost your visibility with competitive bids and stay in control with a weekly or monthly budget
                        cap.
                    </div>
                </div>
                <StrategyTable
                    data={strategyData}
                    onChange={(d) => setStrategyData((prev) => ({ ...prev, ...d }))}
                    onValidationChange={setStrategyTableErrors}
                    isInGroup
                />
            </div>

            {/* Advanced settings */}
            <div className={s.advancedCard}>
                <div className={s.advancedHeader}>
                    <div className={s.advancedHeaderLeft}>
                        <div className={s.advancedTitle}>Advanced settings</div>
                        <div className={s.advancedSubtitle}>
                            Review how your budget is distributed and override bids for high-value specialties.
                        </div>
                        <div className={s.advancedSummary}>
                            <svg
                                className={s.advancedSummaryIcon}
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="var(--main-color, #00816b)"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M2 13V8M6 13V4M10 13V6M14 13V2" />
                            </svg>
                            <span>
                                <strong>
                                    ${budgetSummary.cap.toLocaleString()}/{budgetSummary.periodLabel}
                                </strong>{' '}
                                across{' '}
                                <strong>
                                    {budgetSummary.providerCount} of {stdListingCount} providers
                                </strong>{' '}
                                on Pro-Listing
                            </span>
                            {budgetSummary.providerCount > 0 && (
                                <span className={s.advancedSummaryPer}>
                                    ${budgetSummary.perProvider.toLocaleString()}/provider
                                </span>
                            )}
                        </div>
                    </div>
                    <Switch checked={advancedSettings} onChange={changeAdvancedSettings} />
                </div>
                {advancedSettings && (
                    <div className={s.advancedPanel}>
                        {proListingCount >= 5 && activeProviderCount < 5 && (
                            <div className={s.groupPageWarning}>
                                <InfoCircleFilled className={s.groupPageWarningIcon} />
                                <div className={s.groupPageWarningText}>
                                    Your group practice page is no longer live. Keeping it live requires at least 5
                                    active Pro-Listing providers, and you have fewer than 5. Without a group practice
                                    page, your entire practice&apos;s visibility will be limited.
                                </div>
                            </div>
                        )}
                        {/* Budget Distribution */}
                        <GroupBudgetDistribution
                            providers={providerRows}
                            budgetExcluded={budgetExcluded}
                            budgetSummary={budgetSummary}
                            stdListingCount={stdListingCount}
                            hasJoinedProListing={!!hasJoinedProListing}
                            activeProviderCount={activeProviderCount}
                            poolExhausted={poolExhausted}
                            onToggleProvider={toggleBudgetProvider}
                        />

                        {/* Specialty overrides */}
                        <GroupSpecialtyOverrides
                            providers={providerRows}
                            baseBid={strategyData.generalBidAmount}
                            budgetCap={strategyData.budgetCap || 0}
                            budgetPeriod={strategyData.budgetPeriod}
                            budgetExcluded={budgetExcluded}
                            initialOverrides={initialOverrides}
                            onChange={handleOverridesChange}
                        />
                    </div>
                )}
            </div>

            {/* Footer actions */}
            <StickyFooterActions>
                <Button onClick={() => setShowWebinarBooking(true)}>Talk to a Pro-Listings specialist</Button>
                <Button type="primary" onClick={handleSave}>
                    {isPreLaunch ? 'Launch campaign' : 'Save changes'}
                </Button>
            </StickyFooterActions>

            {/* Confirm save modal */}
            <GroupConfirmSaveModal
                open={confirmSaveModalOpen}
                onCancel={() => setConfirmSaveModalOpen(false)}
                strategyData={strategyData}
                providers={providerRows}
                budgetExcluded={budgetExcluded}
                stdListingCount={stdListingCount}
                overrides={specialtyOverrides}
                onConfirm={() => {
                    refetchPoolBudget();
                    refetchOverview();
                    refetchProviderRows(getDateRange(4));
                }}
            />

            {/* Payment info modal */}
            <Modal
                open={paymentInfoModalOpen}
                onCancel={() => setPaymentInfoModalOpen(false)}
                footer={null}
                width={560}
                title={
                    <div className="flex items-center gap-2">
                        Add payment information <img src={StripeIcon} alt="stripe" className={s.stripeIcon} />
                    </div>
                }
                destroyOnClose
            >
                <div className={s.paymentInfoDesc}>
                    Enter your payment details to start Pro-Listings. You&apos;ll only be charged when a new patient
                    books through your listing.
                </div>
                <PaymentContent
                    confirmButtonText="Continue to confirmation"
                    onSuccess={() => {
                        setPaymentInfoModalOpen(false);
                        setConfirmSaveModalOpen(true);
                    }}
                    hidePolicy
                    isPPV
                />
            </Modal>

            {/* Webinar booking */}
            <WebinarBooking
                modalHeader={
                    <div className={s.wHeader}>
                        <div className={s.wTitle}>Talk to a Pro-Listings specialist</div>
                        <div className={s.wDesc}>
                            Want to grow faster with pro listings? Book a time to speak with our specialist and get
                            personalized guidance.
                        </div>
                    </div>
                }
                show={showWebinarBooking}
                link={CALENDLY_LINK.PRO_LISTINGS_SPECIALIST}
                webinarScheduleType={EWebinarScheduleType.PRO_LISTINGS_SPECIALIST}
                onClose={() => setShowWebinarBooking(false)}
            />

            {/* Add Provider modal */}
            <AddProviderModal open={addProviderOpen} onClose={() => setAddProviderOpen(false)} />
        </div>
    );
};

export default GroupCampaignSetup;
